/* eslint-disable new-cap */
const PictViewClass = require('pict-view');

/** @typedef {import('./HeadlightPanels-View.js').Pict} Pict */

class ObservationSet extends PictViewClass
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;

		this.IncludedObservationSets = [];
		this.ActiveObservationSetID = null;
		this.ActiveObservationSet = null;
		this.ActiveObservationSetAddCallback = (pIDObservation, ActiveObservationSet)=>
		{
			if (this.pict.views?.['PictFormMetacontroller-DynamicInputs'])
			{
				this.pict.views['PictFormMetacontroller-DynamicInputs'].marshalToViewAsync(()=>
				{});
			}
		};
		this.ActiveObservationSetRemoveCallback = (pIDObservation, ActiveObservationSet)=>
		{
			if (this.pict.views?.['PictFormMetacontroller-DynamicInputs'])
			{
				this.pict.views['PictFormMetacontroller-DynamicInputs'].marshalToViewAsync(()=>
				{});
			}
		};
	}

	/**
	 * Get the active observation set from the provider
	 * @param {string} [pSetID] - The observation set id to get
	 * @return {Promise<boolean>} - Returns true if the active observation set is set
	 */
	async getActiveObservationSet(pSetID)
	{
		if (!this.ActiveObservationSetID)
		{
			this.pict.log.warn('ObservationSet.getActiveObservationSet: ActiveObservationSetID is not defined');
			return;
		}
		this.ActiveObservationSet = await this.pict.providers.ObservationSetProvider.loadSet(this.ActiveObservationSetID);
		return this.ActiveObservationSet;
	}

	/**
	 * Render the observation set view before the main render function is called
	 * @param {import('pict-view').ErrorCallback} pCallback - The callback to call after rendering
	 * @param {import('pict-view').Renderable} pRenderable - The renderable to use for rendering
	 * @return {void}
	 */
	onBeforeRenderAsync(pCallback, pRenderable)
	{
		this.getActiveObservationSet().catch((err) =>
		{
			this.pict.log.error('ObservationSet.onBeforeRenderAsync: Error getting active observation set', err);
		}).finally(() =>
		{
			// set the intent for the sub image selector view
			if (this.pict.views?.ObservationSubImageSelector?.intent)
			{
				this.pict.views.ObservationSubImageSelector.intent =
				{
					type: 'ObservationSet',
					IDObservationSet: this.ActiveObservationSetID,
				};
			}
			super.onBeforeRenderAsync(pCallback, pRenderable);
		});
	}

	/**
	 * Handle the click event for the manage set button in the form selector addon - a button can use this to set the active observation set by passing the id
	 * @param {string} pValue - The observation set id to manage
	 * @param {Function} pCallback - The callback to call after setting the active observation set
	 * @return {Promise<void>}
	 */
	async handleFormManageSetButtonClick(pValue, pCallback)
	{
		// if we specifically want to create a new set, do that now
		if (!pValue)
		{
			this.pict.log.error('ObservationSet.handleFormManageSetButtonClick: pValue is required');
			return;
		}
		// set the active observation set to the selected set for rendering
		await this.setObservationSetID(pValue);
		// open the observation panel and switch to the sets tab
		this.pict.views.HeadlightPanels.handlePanelToggle('Observations', 'open', () =>
		{
			this.pict.views.HeadlightPanels.toggleTabContent('HeadlightPanels-ObservationPanel-Sets', 'Observation');
			this.setObservationPanelTarget();
			this.pict.providers.UtilityProvider.removeAllLoadingClasses();
		});
	}

	/**
	 * Add an observation to the active observation set
	 * @param {string} pIDObservation - The observation id to add to the set
	 * @return {Promise<void>}
	 */
	async addObservationToSet(pIDObservation)
	{
		if (!this.ActiveObservationSet)
		{
			this.pict.log.warn('ObservationSet.addObservationToSet: ActiveObservationSet is not defined');
			return;
		}
		if (!pIDObservation)
		{
			this.pict.log.error('ObservationSet.addObservationToSet: pIDObservation is required');
			return;
		}
		// get the observation record from the passed id
		const tmpObservationRecord = await this.pict.providers.ObservationProvider.getObservationByID(pIDObservation);
		// add the observation to the set
		await this.pict.providers.ObservationSetProvider.addObservation(this.ActiveObservationSet, tmpObservationRecord);
		// render the observation set view with the new data
		this.renderAsync('ObservationSet', () =>
		{
			this.pict.log.info('ObservationSet view rendered from addObservationToSet.');
		});
		// if we have a callback, call it
		if (this.ActiveObservationSetAddCallback)
		{
			await this.ActiveObservationSetAddCallback(pIDObservation, this.ActiveObservationSet);
		}
	}

	/**
	 * Handle the removal of an observation from the set via the x button
	 * @param {Event} event - The click event
	 * @param {string} pIDObservation - The observation id to remove from the set
	 * @return {Promise<void>}
	 * @async
	 */
	async handleRemoveObservationFromSet(event, pIDObservation)
	{
		if (event)
		{
			event.preventDefault();
			event.stopPropagation();
		}
		this.pict.log.info('ObservationSet.handleRemoveObservationFromSet: pIDObservation', pIDObservation);
		// find the observation in the set and pass it to the removal function in the provider
		const tmpRemovalObs = this.ActiveObservationSet.Observations.find((obs) => obs.IDObservation == pIDObservation);
		await this.pict.providers.ObservationSetProvider.removeObservation(this.ActiveObservationSet, tmpRemovalObs);
		// render the observation set view with the new data
		this.renderAsync('ObservationSet', () =>
		{
			this.pict.log.info('ObservationSet view rendered from addObservationToSet.');
		});
		// if we have a callback, call it
		if (this.ActiveObservationSetRemoveCallback)
		{
			this.ActiveObservationSetRemoveCallback(pIDObservation, this.ActiveObservationSet);
		}
	}

	/**
	 * Set the observation panel target to the active observation set and enable the "add" mode banner/logic
	 * @return {void}
	 */
	setObservationPanelTarget()
	{
		// set the "add to content" mode on the panel to show the "add" buttons
		document.getElementById('HeadlightPanels-ObservationPanel').setAttribute('data-panel-mode', 'add-to-set');
		// set the panel modes to "add" so that the panel is open
		// TODO: we can handle left vs right later as we evolve, but for now, just set both to "add"
		this.pict.views.HeadlightPanels.ModePanelLeft = 'add-to-set';
		this.pict.views.HeadlightPanels.ModePanelRight = 'add-to-set';
		// set the banner on the header that you are adding an observation
		document.getElementById('HeadlightPanels-ObservationPanel-Observation-Header-Mode-Target').innerText = this.ActiveObservationSet.Name;
		this.pict.views.ObservationSearch.setMode('add-to-set');
	}

	/**
	 * Handle the click event for the add observation button in the observation set view panel - opens the observation panel to search
	 * @param {Event} pEvent - The click event
	 * @return {void}
	 */
	handlePanelAddObservationButtonClick(pEvent)
	{
		// set the tab to the search results
		this.setObservationPanelTarget();
		this.pict.views.HeadlightPanels.toggleTabContent('HeadlightPanels-ObservationPanel-Search', 'Observation');
	}

	/**
	 * Handle the click event for the create media file button in the observation set view panel - opens the observation panel to upload
	 * @param {Event} pEvent - The click event
	 * @return {void}
	 */
	handlePanelCreateObservationButtonClick(pEvent)
	{
		// add the loading class to the button
		if (pEvent && pEvent.currentTarget instanceof HTMLElement)
		{
			pEvent.currentTarget.classList.add('is-loading');
		}
		// open the observation panel and switch to the create tab
		this.pict.views.HeadlightPanels.handlePanelToggle('Observations', 'open', () =>
		{
			this.pict.views.HeadlightPanels.toggleTabContent('HeadlightPanels-ObservationPanel-Upload', 'Observation');
			this.setObservationPanelTarget();
			// remove the loading class from the button
			if (pEvent && pEvent.currentTarget)
			{
				this.pict.providers.UtilityProvider.removeAllLoadingClasses();
			}
		});
	}

	/**
	 * TODO: Handle the click event for the important files button in the observation set view panel - ???
	 * @param {Event} pEvent - The click event
	 * @return {void}
	 */
	handlePanelImportantFilesButtonClick(pEvent)
	{
		// add the loading class to the button
		if (pEvent && pEvent.currentTarget instanceof HTMLElement)
		{
			pEvent.currentTarget.classList.add('is-loading');
		}
		// open the observation panel and switch to the create tab
		this.pict.views.HeadlightPanels.handlePanelToggle('ProjectFileSearch', 'open', () =>
		{
			this.pict.providers.UtilityProvider.removeAllLoadingClasses();
		});
	}

	/**
	 * Set the active observation set id for the view
	 * @param {string} pIDObservationSetID - The observation set id to set as active
	 * @return {Promise<void>} - Returns the active observation set
	 */
	async setObservationSetID(pIDObservationSetID)
	{
		this.ActiveObservationSetID = pIDObservationSetID;
		await this.getActiveObservationSet();
	}

	/**
	 * Reset the observation set view to its default state
	 * @return {void}
	 */
	resetObservationSetView()
	{
		this.ActiveObservationSetID = null;
		this.ActiveObservationSet = null;
		this.ActiveObservationSetAddCallback = null;
		this.ActiveObservationSetRemoveCallback = null;
	}

	/**
	 * Handle the click event for the image preview in the observation set list
	 * @param {Event} pEvent - The click event
	 * @param {string} pIDObservation - The observation id to preview
	 * @return {void}
	 */
	handleImagePreviewClick(pEvent, pIDObservation)
	{
		// add the loading class to the image
		if (pEvent && pEvent.currentTarget instanceof HTMLElement)
		{
			pEvent.currentTarget.classList.add('is-loading');
		}
		// show the observation detail view
		this.pict.log.info('ObservationSet.handleImagePreviewClick: pIDObservation', pIDObservation);
		this.pict.views.HeadlightPaneObservations.showObservationDetailView(pEvent, pIDObservation, null, () =>
		{
			this.pict.providers.UtilityProvider.removeAllLoadingClasses();
		});
	}

	showEditSetNameView()
	{
		this.pict.log.info('ObservationSet.showEditSetNameView: ActiveObservationSet', this.ActiveObservationSet);
		this.renderAsync('ObservationSet-Title-Edit', '#HeadlightPanels-ObservationSet-Title-Wrapper', () =>
		{
			this.pict.log.info('ObservationSet-Title-Edit view rendered.');
		});
	}

	async updateSetName(event)
	{
		// prevent the form from submitting (aka refreshing the page)
		event.preventDefault();
		event.stopPropagation();

		const tmpNewName = document.getElementById('ObservationSet-Edit-Title-Form').querySelector('input').value;
		this.pict.log.info('ObservationSet.updateSetName: tmpNewName', tmpNewName);
		this.ActiveObservationSet.Name = tmpNewName;
		await this.pict.providers.ObservationSetProvider.saveSet(this.ActiveObservationSet);
		this.renderAsync('ObservationSet', '#ObservationSet-ActiveSet-Container', () =>
		{
			this.pict.log.info('ObservationSet view rendered from updateSetName.');
		});
	}

	async updateDisplayedObservationSet(event, observationSet)
	{
		await this.setObservationSetID(observationSet);
		if (event.isDefaultPrevented())
		{
			return;
		}
		this.renderAsync();
	}

	async onAfterRenderAsync(pCallback)
	{
		// OBSERVATION TYPE Selector Filter
		this.initializeSelect2Plugin({
			dropdownCssClass: 'theme-dark has-sticky-option',
			ElementID: 'ObservationSets-IncludedList',
			ParentID: 'HeadlightPanels-ObservationSet-Selector-Container',
			URL: 'ObservationManifests',
			IDField: 'Name',
			TextField: 'DisplayName',
			useAjax: false,
		}, () =>
		{
			console.log('ObservationSet Select2 Initialized.');
		});
		pCallback();
	}

	/**
	 * This method initializes the select2 plugin.
	 * @param {Object} pOptions - The options to use for initialization.
	 * @param {Function} pCallback - The callback function to execute after initialization.
	 */
	async initializeSelect2Plugin(pOptions, pCallback)
	{
		const select2Config = {
			dropdownParent: $('#' + pOptions.ParentID),
			dropdownCssClass: pOptions.dropdownCssClass || 'theme-dark',
			/* @ts-ignore */
			selectionCssClass: pOptions.selectionCssClass || 'theme-dark',
			delay: pOptions.delay || 250,
			placeholder: pOptions.placeholder || 'Select...',
			cache: true,
			minimumInputLength: pOptions.minimumInputLength > -1 ? pOptions.minimumInputLength : this.pict.views.HeadlightPanels.MinimumInputLengthForSearch,
			language: {
				searching: function()
				{
					return pOptions.searchingMessage || 'Searching...';
				},
				noResults: function()
				{
					return pOptions.noResultsMessage || 'No results found.';
				},
				loadingMore: function()
				{
					return pOptions.loadingMoreMessage || 'Loading more results...';
				},
				inputTooShort: function(args)
				{
					return pOptions.inputTooShortMessage || 'Please enter at least ' + args.minimum + ' characters to see results.';
				},
			},
		};

		if (pOptions.useAjax)
		{
			select2Config.ajax = {
				xhrFields: {
					withCredentials: true,
				},
				dataType: 'json',
				method: 'GET',
				url: function(params)
				{
					const tmpSearchQuery = {
						filters: [],
						pagination: {
							start: pOptions?.CurrentPage || 1,
							limit: pOptions?.PageSize || 25,
						},
					};
					// add the search term to the query if it exists
					if (params.term)
					{
						tmpSearchQuery.filters.push({
							field: pOptions.TextField,
							comparator: 'LK',
							target: params.term,
						});
					}
					const startCount = (tmpSearchQuery.pagination.start - 1) * tmpSearchQuery.pagination.limit;
					const endCount = tmpSearchQuery.pagination.start * tmpSearchQuery.pagination.limit;
					return `/1.0/${pOptions.URL}/${this.pict.providers.UtilityProvider.generateMeadowFilter(tmpSearchQuery)}/${startCount}/${endCount}`;
				},
				processResults: function(data, params)
				{
					params.page = params.page || 1;

					const tmpSelections = this.pict.views.ObservationSearch.CurrentSearchFilters[pOptions.URL] || [];

					return {
						results: this.pict.providers.UtilityProvider.formatSelect2Results(data, pOptions.IDField, pOptions.TextField, tmpSelections, pOptions.StickyOption),
						more: params.term ? data.length === this.pict.views.ObservationSearch.CurrentSearchFilters.PageSize : data.length === this.pict.views.ObservationSearch.CurrentSearchFilters.PageSize,
					};
				},
			};
		}
		else
		{
			const IDDocument = this.pict.AppData?.DocumentData?.ReportData?.IDDocument;
			const data = await this.pict.providers.ObservationSetProvider.getObservationSetsForDocument(IDDocument);
			select2Config.data = this.pict.providers.UtilityProvider.formatSelect2Results(data, 'IDObservationSet', 'Name');
		}

		$('#' + pOptions.ElementID).select2(select2Config);

		if (this.ActiveObservationSetID)
		{
			$('#' + pOptions.ElementID).val(this.ActiveObservationSetID);
			const event = $.Event('change');
			event.preventDefault();
			$('#' + pOptions.ElementID).trigger(event);
		}
		// call the callback function if it exists
		if (pCallback)
		{
			pCallback();
		}
	}
}

module.exports = ObservationSet;

module.exports.default_configuration =
{
	ViewIdentifier: 'ObservationSet',
	DefaultRenderable: 'ObservationSet',
	DefaultDestinationAddress: '#ObservationSet-ActiveSet-Container',
	Templates:
		[
			{
				Hash: 'ObservationSet',
				Template: /*html*/`
				{~TIfAbs:ObservationSet-ActiveSet:Context[0]:Context[0].ActiveObservationSetID^>^0~}
				{~TIfAbs:ObservationSet-Unset:Context[0]:Context[0].ActiveObservationSetID^<=^0~}`,
			},
			{
				Hash: 'ObservationSet-Unset',
				Template: /*html*/`
				<div id="HeadlightPanels-ObservationSet-Unset-Message" class="notification has-text-text-light is-dark m-3">
					<p class="has-text-centered">No active observation set.</p>
				</div>`,
			},
			{
				Hash: 'ObservationSet-ActiveSet',
				Template: /*html*/`
				<div id="HeadlightPanels-ObservationSet-Title-Wrapper" class="p-4 mb-0 has-background-text-15">
					{~T:ObservationSet-Title-Display~}
				</div>
				{~T:ObservationSet-Selector~}
				{~TIfAbs:ObservationSet-List:Context[0].ActiveObservationSet.Observations:Context[0].ActiveObservationSet.Observations.length^>^0~}
				{~TIfAbs:ObservationSet-Empty:Context[0].ActiveObservationSet.Observations:Context[0].ActiveObservationSet.Observations.length^<=^0~}
				<div class="buttons is-right m-4">
					<button type="button" class="button is-primary" title="Add Observation" onclick="_Pict.views.ObservationSet.handlePanelAddObservationButtonClick(event)">
						<span class="icon">
							<i class="fas fa-plus"></i>
						</span>
						<span class="icon-text">Add Observation</span>
					</button>
					<button type="button" class="button is-primary" title="Add Observation" onclick="_Pict.views.ObservationSet.handlePanelCreateObservationButtonClick(event)">
						<span class="icon">
							<i class="fas fa-upload"></i>
						</span>
						<span class="icon-text">Create New Observation</span>
					</button>
					<button type="button" class="button is-primary" title="Add Important File" onclick="_Pict.views.ObservationSet.handlePanelImportantFilesButtonClick(event)">
						<span class="icon">
							<i class="fas fa-file"></i>
						</span>
						<span class="icon-text">Include Project File</span>
					</button>
				</div>`,
			},
			{
				Hash: 'ObservationSet-List',
				Template: /*html*/`
				<div id="HeadlightPanels-ObservationSetPanel-List">
					{~TS:ObservationSet-List-Item:Record.Observations~}
				</div>`,
			},
			{
				Hash: 'ObservationSet-List-Item',
				Template: /*html*/`
				<div class="panel-block is-block-flex is-align-items-left is-relative p-2 is-clickable" onclick="_Pict.views.ObservationSet.handleImagePreviewClick(event, '{~D:Record.IDObservation~}')" data-observation-id="{~D:Record.IDObservation~}">
					<a class="preview-image-wrapper">
						<img class="preview-image" src="/1.0/Observation/{~D:Record.IDObservation~}/Image/Thumbnail-Dense{~CacheBustPostfix:~}" alt="Observation Preview for #{~D:Record.IDObservation~}" />
					</a>
					<div class="is-block-flex is-align-items-left mx-4" style="flex-grow: 1">
						<div class="is-size-7 has-text-text-75">#{~D:Record.IDObservation~}</div>
						<div>{~D:Record.Name~}</div>
						<div class="is-size-7 has-text-text-75">{~DateFormat:Record.CreateDate^MMMM DD, YYYY - hh:mm a^~}</div>
						<div class="is-size-7 has-text-text-75"><i class="fa fa-fw fa-user"></i> {~Entity:User^Record.CreatingIDUser^ObservationSet-List-Item-User^~}</div>
					</div>
					<div class="buttons mr-4">
						<button type="button" title="Remove observation from document" class="button is-ghost has-text-text-85 p-0 ml-3" onclick="_Pict.views.ObservationSet.handleRemoveObservationFromSet(event, '{~D:Record.IDObservation~}')">
							<span class="icon">
								<i class="fa fa-fw fa-times-circle"></i>	
							</span>
						</button>
					</div>
				</div>`,
			},
			{
				Hash: 'ObservationSet-Title-Display',
				Template: /*html*/`
				<h1 class="title is-5 has-text-text-90">
					{~D:Context[0].ActiveObservationSet.Name~}
					<a class="ml-1 has-text-link-75" title="Edit Set Name" onclick="_Pict.views.ObservationSet.showEditSetNameView()">
						<span class="icon">
							<i class="fas fa-edit"></i>
						</span>
					</a>
				</h1>`,
			},
			{
				Hash: 'ObservationSet-Title-Edit',
				Template: /*html*/`
				<form id="ObservationSet-Edit-Title-Form" onsubmit="_Pict.views.ObservationSet.updateSetName(event)">
					<div class="field has-addons has-addons-right">
						<div class="control is-expanded">
							<input class="input" type="text" placeholder="Observation Set Name" value="{~D:Context[0].ActiveObservationSet.Name~}">
						</div>
						<div class="control">
							<button id="ObservationSet-Edit-Title-Save-Button" title="Save title" type="submit" class="button is-primary">
								Save
							</button>
						</div>
					</div>
				</form>`,
			},
			{
				Hash: 'ObservationSet-List-Item-User',
				Template: /*html*/`{~D:Record.NameFirst~} {~D:Record.NameLast~}`,
			},
			{
				Hash: 'ObservationSet-Empty',
				Template: /*html*/`
				<div id="HeadlightPanels-ObservationSet-Empty-Message" class="notification has-text-text-light is-dark m-3">
					<p class="has-text-centered">No observations found in this set.</p>
				</div>`,
			},
			{
				Hash: 'ObservationSet-Selector',
				Template: /*html*/`
				<div class="columns p-4">
					<div class="column" id="HeadlightPanels-ObservationSet-Selector-Container">
						<div class="field">
							<label class="label block" for="ObservationSets-IncludedList">Document Included Observation Sets</label>
							<div class="control select is-fullwidth">
								<select 
									id="ObservationSets-IncludedList" 
									onchange="_Pict.views.ObservationSet.updateDisplayedObservationSet(event, this.value)">
								></select>
							</div>
						</div>
					</div>
				</div>`,
			},
		],
	Renderables:
		[
			{
				RenderableHash: 'ObservationSet',
				TemplateHash: 'ObservationSet',
				DestinationAddress: '#ObservationSet-ActiveSet-Container',
				RenderType: 'replace',
			},
			{
				RenderableHash: 'ObservationSet-Title-Display',
				TemplateHash: 'ObservationSet-Title-Display',
				DestinationAddress: '#HeadlightPanels-ObservationSet-Title-Wrapper',
				RenderType: 'replace',
			},
			{
				RenderableHash: 'ObservationSet-Title-Edit',
				TemplateHash: 'ObservationSet-Title-Edit',
				DestinationAddress: '#HeadlightPanels-ObservationSet-Title-Wrapper',
				RenderType: 'replace',
			},
			{
				RenderableHash: 'ObservationSet-Selector',
				TemplateHash: 'ObservationSet-Selector',
				DestinationAddress: '#HeadlightPanels-ObservationSet-Selector-Container',
				RenderType: 'replace',
			},
		],
};
